import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PopupWin from '../PopupWin';
import PopupButton from '../PopupButton';
import * as licenseActions from '../../redux/License/actions';
import moment from 'moment';

import './licenseInformationModal.scss';

const LicenseInformationModal = ({ visible, onClose }) => {
	const dispatch = useDispatch();
	const licenseInfo = useSelector(state => state.license.licenseInfo);
	const loading = useSelector(state => state.license.loading);
	const error = useSelector(state => state.license.error);

	useEffect(() => {
		if (visible) {
			dispatch(licenseActions.loadLicenseInfo());
		}
	}, [visible, dispatch]);

	const formatDate = (dateString) => {
		if (!dateString) return 'N/A';
		return moment(dateString).format('DD.MM.YYYY');
	};

	const formatPackage = (tierName, duration) => {
		if (!tierName) return 'N/A';
		return duration ? `${tierName} - ${duration} days` : tierName;
	};

	const formatStatus = (status) => {
		if (!status) return 'Pending';
		// Capitalize first letter
		return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
	};

	const getStatusClass = (status) => {
		if (!status || status.toLowerCase() === 'pending') return 'status-pending';
		const lowerStatus = status.toLowerCase();
		if (lowerStatus === 'active') return 'status-active';
		if (lowerStatus === 'expired') return 'status-expired';
		return '';
	};

	const renderInfoRow = (label, value, isStatus = false) => {
		if (isStatus) {
			return (
				<div className="license-info-row">
					<div className="license-info-label">{label}:</div>
					<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
						<span className={`license-info-value ${getStatusClass(value)}`}>
							{formatStatus(value)}
						</span>
					</div>
				</div>
			);
		}

		return (
			<div className="license-info-row">
				<div className="license-info-label">{label}:</div>
				<div className="license-info-value">
					{value || 'N/A'}
				</div>
			</div>
		);
	};

	const renderContent = () => {
		if (loading) {
			return (
				<div className="license-loading">
					<div className="loading-spinner"></div>
					<div className="loading-text">Loading license information...</div>
				</div>
			);
		}

		if (error) {
			return (
				<div className="license-error">
					<div className="error-message">
						Failed to load license information. Please try again.
					</div>
					<PopupButton
						variant="primary"
						onClick={() => dispatch(licenseActions.loadLicenseInfo())}
						size="small"
					>
						Retry
					</PopupButton>
				</div>
			);
		}

		// Always show license information, even if licenseInfo is null or incomplete
		const displayLicenseInfo = licenseInfo || {
			client_id: null,
			pricing_tier_name: null,
			pricing_tier_duration: null,
			status: 'pending',
			purchase_date: null,
			expiration_date: null,
			connector_version: null
		};

		return (
			<div className="license-information-content">
				{renderInfoRow('Organization ID', displayLicenseInfo.client_id)}
				{renderInfoRow('Package', formatPackage(displayLicenseInfo.pricing_tier_name, displayLicenseInfo.pricing_tier_duration))}
				{renderInfoRow('Status', displayLicenseInfo.status, true)}
				{renderInfoRow('Purchase date', formatDate(displayLicenseInfo.purchase_date))}
				{renderInfoRow('Expiry date', formatDate(displayLicenseInfo.expiration_date))}
				{renderInfoRow('Connector version', displayLicenseInfo.connector_version)}
			</div>
		);
	};

	const footer = useMemo(() => (
		<PopupButton variant="secondary" onClick={onClose}>
			Cancel
		</PopupButton>
	), [onClose]);

	return (
		<PopupWin
			visible={visible}
			onClose={onClose}
			title="License Information"
			popupStyle={{
				minWidth: '450px',
				maxWidth: '550px'
			}}
			footer={footer}
		>
			{renderContent()}
		</PopupWin>
	);
};

export default LicenseInformationModal; 